import { ButtonSecondary, Colors, FOCUS_RING, Icon, Text } from '@amelio/ui-library';
import { useState } from 'react';

import type { MeetingPlanAgendaItemFormData } from './validation';
import type { MeetingAgendaUpdatePayload } from '@/types/review/PostReviewMeeting';
import type { MeetingTemplateDiscussionPoint } from '@/types/thematic-meetings';
import type { ContentLanguage } from '@/utils/contentLanguage';
import type { LocalizedText } from '@/utils/localization';

import { AgendaEditor } from '@/components/MeetingAgendaBuilder/AgendaEditor';
import { useAgendaEditor } from '@/components/MeetingAgendaBuilder/useAgendaEditor';
import { PlanCard, PlanSectionHeading } from '@/components/MeetingPlan/PlanSection';
import { useAmelioTranslations } from '@/hooks';

/**
 * What a subject of the plan carries that the shared agenda has no place for: its description (kept
 * as it was read — nothing here edits it any more) and the wording of the subject in the languages
 * nobody is retyping. `null` stored text for a subject just added, which has no other language yet.
 */
type AgendaItemAside = Pick<MeetingPlanAgendaItemFormData, 'description' | 'storedText'>;

/** A subject as the section hands it to the form, whichever of the editor's two outputs it is read from. */
type EditedSubject = {
    id: string;
    title: string;
    durationMinutes: number;
    discussionPoints: { id: string; title: string; context: string | null }[];
};

interface MeetingPlanAgendaSectionProps {
    value: MeetingPlanAgendaItemFormData[];
    onChange: (items: MeetingPlanAgendaItemFormData[]) => void;
    language: ContentLanguage;
    /** What the form's own rules refuse about the agenda, said where the agenda is written. */
    errorMessage?: string;
}

const subjectId = (index: number) => `plan-subject-${index}`;
const pointId = (subjectIndex: number, pointIndex: number) =>
    `plan-point-${subjectIndex}-${pointIndex}`;

/** The one language being retyped, or nothing: never the other language's wording in its place. */
const wordingIn = (text: LocalizedText, language: ContentLanguage) => text[language] ?? '';

/**
 * The plan's agenda, written with the very editor a meeting's agenda is written with: one card per
 * subject, a grip to reorder them, the length as a pill, the total the plan adds up to, and inside each
 * subject its discussion points — added in their row, detailed in the discussion point's modal.
 *
 * The shared editor owns the subjects from the moment it mounts and reports every change back, so this
 * section never pushes the form's value into it again — a re-seed would undo what is being typed. The
 * editor edits one language, so it is handed the reader's wording only (never the other language's in
 * its place, which would copy it into a field this company has left empty); the other languages are
 * merged back here, keyed by the subject's and the point's id.
 */
export const MeetingPlanAgendaSection = ({
    value,
    onChange,
    language,
    errorMessage,
}: MeetingPlanAgendaSectionProps) => {
    const { t } = useAmelioTranslations();

    // Read once, on mount. Ids are positional because the saved plan gives its subjects and points
    // none; from here on the editor issues its own for whatever is added.
    const [initialAgenda] = useState(() => ({
        language,
        openingStatement: '',
        items: value.map((item, index) => ({
            id: subjectId(index),
            title: item.title,
            durationMinutes: item.durationMinutes,
            order: index,
            hasHandout: false,
            isAiGenerated: false,
            discussionPoints: item.discussionPoints.map((point, order) => {
                const context = wordingIn(point.context, language);
                return {
                    id: pointId(index, order),
                    title: wordingIn(point.title, language),
                    order,
                    hasDetails: context !== '',
                    context: context === '' ? null : context,
                    dateAdded: '',
                    response: null,
                    isAiGenerated: false,
                };
            }),
        })),
    }));

    const [storedPoints] = useState<Record<string, MeetingTemplateDiscussionPoint>>(() =>
        Object.fromEntries(
            value.flatMap((item, index) =>
                item.discussionPoints.map((point, order) => [pointId(index, order), point]),
            ),
        ),
    );

    // Read once, like `initialAgenda`: nothing in this section edits a subject's description any
    // more, so it is carried through to `onSave` exactly as the plan already had it.
    const [asides] = useState<Record<string, AgendaItemAside>>(() =>
        Object.fromEntries(
            value.map((item, index) => [
                subjectId(index),
                { description: item.description, storedText: item.storedText },
            ]),
        ),
    );

    const withStoredLanguages = (
        point: EditedSubject['discussionPoints'][number],
    ): MeetingTemplateDiscussionPoint => {
        const stored = storedPoints[point.id];
        return {
            title: { ...stored?.title, [language]: point.title },
            context: { ...stored?.context, [language]: point.context ?? '' },
        };
    };

    const publish = (subjects: EditedSubject[]) =>
        onChange(
            subjects.map((subject) => ({
                title: subject.title,
                durationMinutes: subject.durationMinutes,
                description: asides[subject.id]?.description ?? '',
                storedText: asides[subject.id]?.storedText ?? null,
                discussionPoints: subject.discussionPoints.map(withStoredLanguages),
            })),
        );

    // The editor saves through the payload it would send a server; here it feeds the form instead,
    // which is what holds the plan until the page's own save button is pressed.
    const fromPayload = (payload: MeetingAgendaUpdatePayload): EditedSubject[] =>
        payload.items.map((item) => ({
            id: item.id,
            title: item.title,
            durationMinutes: item.durationMinutes,
            discussionPoints: item.discussionPoints.map((point) => ({
                id: point.id,
                title: point.title,
                context: point.context,
            })),
        }));

    const editor = useAgendaEditor({
        agenda: initialAgenda,
        disabled: false,
        onSave: (payload) => publish(fromPayload(payload)),
    });

    const totalMinutes = editor.items.reduce((total, item) => total + item.durationMinutes, 0);

    return (
        // The card alone, not `MeetingPlanSectionCard`: the shared agenda draws its own header, so
        // that its title and the total it adds up to sit on one line.
        <PlanCard dataTestId={'meeting-plan-template-agenda'}>
            <AgendaEditor
                editor={editor}
                totalMeetingTimeMinutes={totalMinutes}
                heading={
                    <PlanSectionHeading
                        title={t('templateManagement.meetingPlanEditor.agendaHeading')}
                    />
                }
                openingStatementEditing={'none'}
                // A plan is written before any meeting: there is no employee to answer yet.
                withResponses={false}
                itemListDataTestId={'meeting-plan-template-agenda-items'}
                renderItemActions={(item) => (
                    <button
                        type={'button'}
                        onClick={() => editor.topics.remove(item.id)}
                        aria-label={t('templateManagement.meetingPlanEditor.removeAgendaItem')}
                        className={`${FOCUS_RING} hover:bg-primary/10 mt-3 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full`}
                        data-testid={`meeting-plan-template-agenda-remove-${item.id}`}
                    >
                        <Icon
                            name={'essential-trash'}
                            size={'sm'}
                            color={Colors.BASE_500}
                            ariaHidden
                        />
                    </button>
                )}
                addTopicControl={
                    <ButtonSecondary
                        onClick={() => editor.topics.setIsAdding(true)}
                        disabled={!editor.topics.canAdd}
                        className={'mt-4'}
                        fitContent
                        leftIcon={
                            <Icon
                                name={'essential-add'}
                                size={'lg'}
                                color={Colors.PRIMARY_900}
                                ariaHidden
                            />
                        }
                        dataTestId={'meeting-plan-template-add-agenda-item'}
                    >
                        {t('templateManagement.meetingPlanEditor.addAgendaItem')}
                    </ButtonSecondary>
                }
            />

            {errorMessage && (
                <div
                    role={'alert'}
                    className={'mt-4'}
                >
                    <Text
                        size={'small'}
                        color={Colors.ERROR_800}
                    >
                        {errorMessage}
                    </Text>
                </div>
            )}
        </PlanCard>
    );
};
